// src/features/media/components/MediaGridRow.tsx

import { memo, type CSSProperties, type MouseEvent } from 'react';
import { IconDots } from '@tabler/icons-react';
import type { VideoGridRow } from '@/lib/types/Media';
import styles from './MediaGrid.module.css';

interface MediaGridRowProps {
  video: VideoGridRow;
  index: number;
  isSelected: boolean;
  /** Active job percent (0-100) for non-ready media; undefined = no active job. */
  processingPercent?: number;
  onSelectOnly: (id: number, index: number) => void;
  onToggle: (id: number, index: number) => void;
  onRangeSelect: (id: number, index: number) => void;
  onMenuOpen: (index: number, buttonEl: HTMLElement) => void;
  onNavigate: (href: string) => void;
  onPrefetch?: (video: VideoGridRow) => void;
  style: CSSProperties;
}

/**
 * Single flat row component — all content inlined as native HTML elements.
 * Zero Mantine/Router component instances per row for minimal reconciliation cost.
 * The shared Menu component lives in MediaGrid (single instance).
 */
function MediaGridRowComponent({
  video,
  index,
  isSelected,
  processingPercent,
  onSelectOnly,
  onToggle,
  onRangeSelect,
  onMenuOpen,
  onNavigate,
  onPrefetch,
  style,
}: MediaGridRowProps) {
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    // Checkbox click - toggle selection
    if (target.closest('[data-cell="checkbox"]')) {
      onToggle(video.mediaId, index);
      return;
    }

    // Actions button click - let the button handler deal with it
    if (target.closest('[data-cell="actions"]')) {
      return;
    }

    // Title link click - let it handle navigation
    if (target.closest('[data-cell="title"] a')) {
      return;
    }

    // Row selection with modifier keys
    if (e.shiftKey) {
      e.preventDefault();
      onRangeSelect(video.mediaId, index);
    } else if (e.ctrlKey || e.metaKey) {
      onToggle(video.mediaId, index);
    } else {
      onSelectOnly(video.mediaId, index);
    }
  };

  const handleTitleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Allow right-click / middle-click to open in new tab (native <a> behavior)
    // Left-click does client-side navigation
    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      onPrefetch?.(video);
      e.preventDefault();
      e.stopPropagation();
      onNavigate(video.href);
    }
  };

  const handleTitleMouseDown = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0) return;
    onPrefetch?.(video);
  };

  return (
    <div
      className={`${styles.row} ${styles.gridColumns} ${isSelected ? styles.rowSelected : ''}`}
      style={style}
      onClick={handleClick}
      data-selected={isSelected}
      data-media-grid-row="true"
    >
      {/* Checkbox — CSS-only custom checkbox */}
      <div className={`${styles.cell} ${styles.cellCheckbox}`} data-cell="checkbox">
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            checked={isSelected}
            readOnly
            tabIndex={-1}
            aria-label={`Select ${video.title}`}
            onClick={(e) => e.stopPropagation()}
          />
          <span className={styles.checkboxControl} />
        </label>
      </div>

      {/* Thumbnail — inlined from MediaThumbnail */}
      <div className={`${styles.cell} ${styles.cellThumbnail}`}>
        <div className={styles.thumbContainer}>
          {video.thumbnailUrl ? (
            <img
              src={video.thumbnailUrl}
              alt={video.title}
              className={styles.thumbImage}
              loading="lazy"
            />
          ) : (
            <div className={styles.thumbPlaceholder}>
              <span className={styles.thumbPlaceholderText}>N/A</span>
            </div>
          )}
          {video.duration > 0 && (
            <span className={styles.thumbDuration}>
              {video.durationFormatted}
            </span>
          )}
        </div>
      </div>

      {/* Title — plain <a> with client-side navigation callback */}
      <div className={`${styles.cell} ${styles.cellTitle}`} data-cell="title">
        <div className={styles.titleContainer}>
          <a
            href={video.href}
            className={styles.titleLink}
            onMouseDown={handleTitleMouseDown}
            onClick={handleTitleClick}
          >
            {video.title}
          </a>
          <span className={styles.titleId}>{video.displayId}</span>
        </div>
      </div>

      {/* Status — CSS-only badge (with live job percent while processing) */}
      <div className={`${styles.cell} ${styles.cellStatus}`} data-col="status">
        <span className={styles.statusBadge} data-color={video.statusColor}>
          {video.statusLabel}
          {processingPercent !== undefined && ` · ${Math.round(processingPercent)}%`}
        </span>
      </div>

      {/* Published */}
      <div className={`${styles.cell} ${styles.cellDate}`} data-col="published">
        {video.publishedFormatted ? (
          <span title={video.publishedTooltip ?? undefined}>{video.publishedFormatted}</span>
        ) : '-'}
      </div>

      {/* Updated */}
      <div className={`${styles.cell} ${styles.cellDate}`} data-col="updated">
        <span title={video.updatedTooltip}>{video.updatedFormatted}</span>
      </div>

      {/* Kebab — keep IconDots (trivial SVG, no hooks) */}
      <div className={`${styles.cell} ${styles.cellAction}`} data-cell="actions">
        <button
          className={styles.kebabButton}
          onClick={(e) => { e.stopPropagation(); onMenuOpen(index, e.currentTarget); }}
          aria-label="Actions"
          type="button"
        >
          <IconDots size={16} />
        </button>
      </div>
    </div>
  );
}

// Memoize row — virtualization reuses row slots, comparator keeps updates focused.
export const MediaGridRow = memo(MediaGridRowComponent, (prev, next) => {
  return (
    prev.video === next.video &&
    prev.index === next.index &&
    prev.isSelected === next.isSelected &&
    prev.processingPercent === next.processingPercent &&
    prev.style.transform === next.style.transform &&
    prev.style.height === next.style.height
  );
});
